import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { fmtEuro, MACRO_TIPOLOGIA } from "@/lib/constants";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

export default function Report() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: corsi } = await api.get("/corsi");
      const out = [];
      for (const c of corsi) {
        try {
          const { data } = await api.get(`/corso/${c.id}/full`);
          out.push({
            id: c.id, codice: c.codice, titolo: c.titolo, tipo: c.macro_tipologia,
            preventivo: data.calc.preventivo_totale || 0,
            consuntivo: data.calc.consuntivo_totale || 0,
            scostamento: data.calc.scostamento || 0,
          });
        } catch (e) {}
      }
      setRows(out);
    })();
  }, []);

  if (!rows)
    return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const chartData = rows.map((r) => ({ name: r.codice, Preventivo: r.preventivo, Consuntivo: r.consuntivo }));
  const totPrev = rows.reduce((a, r) => a + r.preventivo, 0);
  const totCons = rows.reduce((a, r) => a + r.consuntivo, 0);

  const exportCsv = () => {
    const header = ["Codice", "Titolo", "Tipologia", "Preventivo", "Consuntivo", "Scostamento"];
    const data = rows.map((r) => [r.codice, r.titolo, MACRO_TIPOLOGIA[r.tipo]?.label || r.tipo, r.preventivo, r.consuntivo, r.scostamento]);
    const csv = [header, ...data].map((r) => r.map((x) => `"${x}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "report_costi_indar.csv"; a.click();
    toast.success("Report esportato");
  };

  return (
    <div data-testid="report-page">
      <PageHeader title="Report & Costi" subtitle="Preventivo vs Consuntivo per corso">
        <Button variant="outline" onClick={exportCsv} data-testid="export-report-btn">
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        <div className="rounded-xl border bg-white p-5">
          <div className="text-xs font-semibold uppercase text-slate-500">Preventivo Totale</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{fmtEuro(totPrev)}</div>
        </div>
        <div className="rounded-xl border bg-white p-5">
          <div className="text-xs font-semibold uppercase text-slate-500">Consuntivo Totale</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{fmtEuro(totCons)}</div>
        </div>
        <div className="rounded-xl border bg-white p-5">
          <div className="text-xs font-semibold uppercase text-slate-500">Scostamento</div>
          <div className={`mt-1 font-display text-2xl font-bold ${totCons - totPrev > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtEuro(totCons - totPrev)}</div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-5">
        <h3 className="font-display text-lg font-semibold text-slate-900">Confronto per corso</h3>
        <div className="mt-4 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => fmtEuro(v)} cursor={{ fill: "#f8fafc" }} />
              <Legend />
              <Bar dataKey="Preventivo" fill="#94A3B8" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Consuntivo" fill="#E11D48" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
